import React, { useState } from "react";
import {
  Landmark,
  CreditCard,
  CheckCircle2,
  Circle,
  ChevronLeft,
  Loader2,
  ShieldAlert,
} from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";

const SectionLabel = ({ children }) => (
  <h2 className="text-[9px] font-semibold uppercase tracking-[0.2em] text-[#0a2540]/40">
    {children}
  </h2>
);

const FieldLabel = ({ children }) => (
  <label className="block text-[10px] uppercase tracking-[0.18em] font-semibold text-[#0A4730]/70 mb-2">
    {children}
  </label>
);

const INPUT_CLASS =
  "w-full rounded-xl bg-white border border-slate-200 px-4 py-3 text-sm text-[#0A2540] placeholder:text-slate-400 focus:outline-none focus:border-[#0A4730] focus:ring-4 focus:ring-[#0A4730]/5 transition-all duration-200";

// ---------------------------------------------------------------------------
// Funding source types. Bank is a plain fields-in-a-form flow like recipient
// creation. Card is NOT wired to raw text inputs on purpose — see the notice
// rendered in that branch below.
// ---------------------------------------------------------------------------
const SOURCE_TYPES = {
  bank: {
    label: "Bank Account",
    icon: Landmark,
    fields: [
      { key: "account_holder_name", label: "Account Holder Name", type: "text" },
      { key: "account_number", label: "Account Number", type: "text" },
      { key: "sort_code", label: "Sort Code", type: "text", placeholder: "20-00-00" },
    ],
  },
  card: {
    label: "Debit / Credit Card",
    icon: CreditCard,
    fields: [], // intentionally empty — see notice
  },
};

export function CreateFundingSource() {
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = location.state?.returnTo || "/transfer";

  const [sourceType, setSourceType] = useState(null);
  const [values, setValues] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const activeType = sourceType ? SOURCE_TYPES[sourceType] : null;

  function setValue(key, val) {
    setValues((prev) => ({ ...prev, [key]: val }));
  }

  async function handleSubmit() {
    setSubmitError(null);
    if (!sourceType) return setSubmitError("Choose how you'd like to pay.");

    if (sourceType === "card") {
      // Deliberately not implemented: card details must go through a
      // tokenizing provider's client-side SDK (e.g. Stripe Elements) so raw
      // card numbers/CVVs never touch this form or this backend. This branch
      // is a placeholder until that integration exists.
      setSubmitError(
        "Card payments aren't available yet — this needs a secure card-entry integration first."
      );
      return;
    }

    for (const field of activeType.fields) {
      if (!values[field.key]) {
        setSubmitError("Please fill in all fields.");
        return;
      }
    }

    setSubmitting(true);
    try {
      // TODO: wire this up once the backend funding-source endpoint exists —
      // e.g. createFundingSourceAsync({ type: sourceType, ...values }) via a
      // new useMe() mutation. For now this just round-trips a plain object
      // back to the caller via nav state, matching the CurrencyPicker pattern.
      const fundingSource = {
        id: `local-${Date.now()}`,
        type: sourceType,
        label:
          sourceType === "bank"
            ? `${values.account_holder_name || "Bank account"} · ${values.account_number?.slice(-4) || ""}`
            : activeType.label,
        ...values,
      };

      navigate(returnTo, { state: { selectedFundingSource: fundingSource } });
    } catch {
      setSubmitError("Something went wrong saving this funding source.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-[#FDFDFD] pb-24 text-left">

      {/* HEADER — mobile only */}
      <div className="md:hidden bg-[#0A4730] rounded-b-[50px] px-6 pt-12 pb-8">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center active:scale-95 transition"
          >
            <ChevronLeft className="text-white" size={20} />
          </button>
          <div>
            <p className="text-[12px] text-white opacity-80 mb-1">Payments</p>
            <h1 className="text-2xl text-[#C0F6B7] font-serif font-semibold leading-tight">
              How You'll Pay
            </h1>
          </div>
        </div>
      </div>

      {/* DESKTOP HEADER */}
      <div className="hidden md:flex items-center gap-3 max-w-xl mx-auto px-5 pt-10 pb-2">
        <button
          onClick={() => navigate(-1)}
          className="w-9 h-9 rounded-full border border-slate-200 bg-white flex items-center justify-center active:scale-95 transition text-[#0A4730]/60 hover:border-[#0A4730]/30"
        >
          <ChevronLeft size={18} />
        </button>
        <h1 className="text-2xl text-[#0A4730] font-serif font-semibold">
          How You'll Pay
        </h1>
      </div>

      <div className="max-w-xl mx-auto px-5 py-8 space-y-8">

        {/* Source type */}
        <div className="space-y-2.5">
          <SectionLabel>Funding Source</SectionLabel>
          <div className="bg-white rounded-3xl p-6 border border-slate-100 shadow-[0_4px_20px_-5px_rgba(0,0,0,0.05)] space-y-2.5">
            {Object.entries(SOURCE_TYPES).map(([key, type]) => {
              const Icon = type.icon;
              return (
                <SelectionCard
                  key={key}
                  active={sourceType === key}
                  onClick={() => { setSourceType(key); setValues({}); setSubmitError(null); }}
                  icon={<Icon size={18} />}
                  label={type.label}
                />
              );
            })}
          </div>
        </div>

        {/* Bank fields */}
        {sourceType === "bank" && (
          <div className="space-y-2.5">
            <SectionLabel>Account Details</SectionLabel>
            <div className="bg-white rounded-3xl p-6 border border-slate-100 shadow-[0_4px_20px_-5px_rgba(0,0,0,0.05)] space-y-4">
              {activeType.fields.map((field) => (
                <div key={field.key}>
                  <FieldLabel>{field.label}</FieldLabel>
                  <input
                    type="text"
                    value={values[field.key] || ""}
                    onChange={(e) => setValue(field.key, e.target.value)}
                    placeholder={field.placeholder || ""}
                    className={INPUT_CLASS}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Card — not implemented, needs a tokenizing provider */}
        {sourceType === "card" && (
          <div className="flex gap-3 items-start p-4 bg-amber-50 rounded-2xl border border-amber-100">
            <ShieldAlert className="text-amber-600 shrink-0 mt-0.5" size={18} />
            <p className="text-[12px] text-amber-800 leading-relaxed">
              Card entry isn't wired up yet. Card numbers and CVVs need to go through
              a secure, tokenizing card-entry integration (e.g. Stripe Elements) —
              never as plain text fields submitted to our own backend. This screen
              is a placeholder until that integration is built.
            </p>
          </div>
        )}

        {submitError && (
          <div className="rounded-2xl bg-red-50 border border-red-100 p-4 text-red-500 text-sm">
            {submitError}
          </div>
        )}

        <button
          disabled={submitting || !sourceType}
          onClick={handleSubmit}
          className="w-full h-14 rounded-2xl bg-[#0A4730] text-white font-bold tracking-wide hover:bg-[#0A4730]/90 transition-all duration-300 active:scale-[0.98] disabled:opacity-60 flex items-center justify-center gap-2"
        >
          {submitting && <Loader2 size={16} className="animate-spin" />}
          {submitting ? "Saving…" : "Save"}
        </button>

      </div>
    </div>
  );
}

function SelectionCard({ active, icon, label, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full flex items-center justify-between px-5 py-4 rounded-2xl border transition duration-200 ${
        active
          ? "bg-[#0A4730] border-[#0A4730] text-white shadow-sm"
          : "bg-[#C0F6B7]/40 border-[#0A4730]/10 text-[#0A4730]"
      }`}
    >
      <div className="flex items-center gap-3">
        <span className={active ? "text-white" : "text-[#0A4730]/70"}>{icon}</span>
        <span className="text-sm font-medium">{label}</span>
      </div>
      {active
        ? <CheckCircle2 size={18} className="text-white" />
        : <Circle size={18} className="text-[#0A4730]/20" />}
    </button>
  );
}